/**
 * HelpLink — small inline "→ App Guide" link sprinkled into tooltipped
 * controls across the app. Clicking opens the App Guide in a new tab at
 * the specified section anchor.
 *
 * Used as a one-line addition at the bottom of a tooltip body or next
 * to a section header to give the user a fast path to the comprehensive
 * docs while keeping the in-app tooltip terse.
 */
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';

interface Props {
  /** Hash inside /app-guide — e.g. "risk-management/daily-cap" or "order-flow". */
  to: string;
  /** Display label. Defaults to "Open App Guide". */
  label?: string;
  /** Render mode. "icon" = compact icon button. "inline" = small chip-like text link. */
  variant?: 'icon' | 'inline';
}

const HelpLink: React.FC<Props> = ({ to, label = 'Open App Guide', variant = 'inline' }) => {
  const href = `/app-guide#${to}`;
  const handle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'icon') {
    return (
      <Tooltip arrow title={label}>
        <Box
          component="a"
          href={href}
          onClick={handle}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ml: 0.5,
            p: 0.25,
            color: 'text.secondary',
            cursor: 'pointer',
            borderRadius: 0.5,
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
          aria-label={label}
        >
          <MenuBookIcon sx={{ fontSize: 14 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component="a"
      href={href}
      onClick={handle}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        fontSize: '0.72rem',
        color: 'primary.main',
        textDecoration: 'none',
        fontWeight: 600,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <MenuBookIcon sx={{ fontSize: 12 }} />
      {label}
    </Box>
  );
};

export default HelpLink;
